import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook to guard navigation away from configuration screens
 * @param {string[]} allowedPaths - Paths that are allowed without confirmation (next/back in flow)
 * @param {function} onConfirmExit - Callback when user confirms exit (for cleanup)
 * @returns {object} - Modal state and handlers
 */
export function useConfigNavigationGuard(allowedPaths = [], onConfirmExit) {
    const [showExitModal, setShowExitModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const originalGuardedNavigate = window.guardedNavigate;

        window.guardedNavigate = (pathOrFn, options) => {
            // If it's a function, we can't check allowedPaths, so we assume it needs confirmation
            // unless we want to support some way to bypass.
            // For now, assume functions always need confirmation if we are in a guarded state.

            if (typeof pathOrFn === 'string' && allowedPaths.includes(pathOrFn)) {
                if (originalGuardedNavigate) {
                    originalGuardedNavigate(pathOrFn, options);
                } else {
                    navigate(pathOrFn, options);
                }
                return;
            }

            // For other navigation (or functions), show confirmation modal
            setPendingNavigation({ target: pathOrFn, options });
            setShowExitModal(true);
        };

        return () => {
            window.guardedNavigate = originalGuardedNavigate;
        };
    }, [allowedPaths, navigate]);

    const handleConfirmExit = () => {
        if (onConfirmExit) {
            onConfirmExit();
        }
        setShowExitModal(false);
        if (pendingNavigation) {
            if (typeof pendingNavigation.target === 'function') {
                pendingNavigation.target();
            } else {
                navigate(pendingNavigation.target, pendingNavigation.options);
            }
            setPendingNavigation(null);
        }
    };

    const handleCancelExit = () => {
        setShowExitModal(false);
        setPendingNavigation(null);
    };

    return {
        showExitModal,
        handleConfirmExit,
        handleCancelExit
    };
}
